import React, { useState } from 'react';
import { TextField, Button, Typography, Box } from '@mui/material';
import { useAuth } from '@/lib/firebase/auth';
import { getAuth, getMultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth'; // Import necessary Firebase modules
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [verificationCode, setVerificationCode] = useState(''); // For MFA code
  const [resolver, setResolver] = useState(null); // Store the resolver for MFA
  const { login } = useAuth(); // Access the login function
  const navigate = useNavigate(); // Initialize useNavigate for redirection

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    try {
      // Attempt normal login process
      const userCredential = await login({ email, password });
      
      // Login was successful, no MFA required
    } catch (error) {
      console.error("Full error object:", error); // Log the full error object

      // Check if the error is due to multi-factor authentication
      if (error.code === 'auth/multi-factor-auth-required') {
        try {
          console.log("Multi-factor error received:", error);
          
          // Ensure the error has the required customData property before accessing it
          if (error.customData && error.customData._serverResponse) {
            const resolver = getMultiFactorResolver(getAuth(), error);
            setResolver(resolver); // Store the MFA resolver for later use
          } else {
            setError('Multi-factor authentication error, missing customData.');
            console.error("Error object is missing customData:", error);
          }
        } catch (resolverError) {
          console.error('Error obtaining multi-factor resolver:', resolverError);
          setError('Multi-factor authentication failed. Please try again.');
        }
      } else {
        setError(error.message); // Set error message for non-MFA related errors
      }
    }
  };

  const handleVerifyMFA = async () => {
    setError(null);
    try {
      // Ensure we're working with the correct MFA factor (TOTP)
      const selectedFactor = resolver.hints.find(factor => factor.factorId === 'totp');
      
      if (!selectedFactor) {
        throw new Error("No TOTP factor found in the resolver hints.");
      }
  
      console.log("Selected factor UID:", selectedFactor.uid); // Log the factor UID for debugging
      console.log("Entered verification code:", verificationCode); // Log the entered TOTP code
  
      // Ensure the uid is passed correctly to the assertionForSignIn
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        selectedFactor.uid, // Use the correct uid of the enrolled TOTP factor
        verificationCode
      );
  
      // Complete the multi-factor sign-in
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
      console.log("MFA sign-in successful!", userCredential);
      navigate('/'); // Redirect to the homepage after successful MFA

  
    } catch (error) {
      console.error("MFA verification error:", error); // Log the full error
      setError(`MFA verification failed: ${error.message}`);
    }
  };
  

  return (
    <Box p={3} borderRadius={4} maxWidth={420} boxShadow={3} mx="auto" mt={8}>
      <Typography variant="h4" align="center" mb={3}>
        Login
      </Typography>
      
      {/* If resolver is null, show email/password form */}
      {!resolver ? (
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
          <Button type="submit" variant="contained" fullWidth mt={2}>
            Login
          </Button>
        </form>
      ) : (
        // Show MFA verification form if resolver is set
        <Box>
          <Typography variant="body1" align="center" mb={2}>
            Multi-Factor Authentication Required
          </Typography>
          <TextField
            label="Enter MFA Code"
            fullWidth
            margin="normal"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
          <Button onClick={handleVerifyMFA} variant="contained" fullWidth>
            Verify MFA Code
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Login;
